import { useCallback, useEffect, useState } from "react";
import { Building2, Globe2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { EntitySheet } from "@/components/common/EntitySheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUNTRY_NAME, shortDate } from "@/lib/format";

export default function OrgSetup() {
  const { can } = useAuth();
  const [orgs, setOrgs] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [entity, setEntity] = useState(null);
  const [entityDetail, setEntityDetail] = useState(null);
  const [register, setRegister] = useState(null);

  const openEntity = async (org) => {
    setEntity(org);
    setEntityDetail(null);
    try {
      const { data } = await api.get(`/charity/organizations/${org.id}`);
      setEntityDetail(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/charity/organizations");
    setOrgs(data);
    const parent = data.find((o) => o.entity_level === "parent") || data[0];
    setForm(parent || null);
  }, []);

  useEffect(() => { load().catch(() => setOrgs([])); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/charity/organizations/${form.id}`, {
        legal_name: form.legal_name, display_name: form.display_name, country: form.country,
        timezone: form.timezone, base_currency: form.base_currency, legal_form: form.legal_form,
        charity_classification: form.charity_classification, tax_status: form.tax_status,
        status: form.status, parent_id: form.parent_id, entity_level: form.entity_level,
        registers: form.registers || [],
      });
      toast[res.data?.pending_approval ? "message" : "success"](
        res.data?.message || "Organization profile updated — regulatory profile re-evaluated");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  if (!orgs || !form) return <Loading label="Loading organization profile" />;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-002 · Organization Setup"
        title="Legal entity, hierarchy and status registers"
        description="The organization's country, legal form, classification and tax status drive layers 3–7 of the applicability cascade. Registers carry independent effective dates."
        actions={can("organization", "write") && (
          <Button onClick={save} disabled={busy} data-testid="org-save-button"
                  className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
            Save profile
          </Button>
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-org-entities" label="Legal entities" value={orgs.length} accent="sky" icon={Building2} />
        <Kpi testId="kpi-org-country" label="Primary jurisdiction" value={form.country} accent="violet" icon={Globe2}
             sub={COUNTRY_NAME[form.country] || ""} />
        <Kpi testId="kpi-org-form" label="Legal form" value={form.legal_form} accent="emerald" />
        <Kpi testId="kpi-org-registers" label="Status registers" value={(form.registers || []).length} accent="amber" />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel testId="panel-org-profile" kicker="FR-ORG-001 / 003" title="Legal entity profile">
          <div className="grid grid-cols-2 gap-3 p-4">
            <F label="Legal name" full>
              <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                     data-testid="org-legalname-input" className="bg-muted/40" />
            </F>
            <F label="Display name" full>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                     data-testid="org-displayname-input" className="bg-muted/40" />
            </F>
            <F label="Country">
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
                     data-testid="org-country-input" className="bg-muted/40 font-mono" />
            </F>
            <F label="Base currency">
              <Input value={form.base_currency}
                     onChange={(e) => setForm({ ...form, base_currency: e.target.value.toUpperCase() })}
                     data-testid="org-currency-input" className="bg-muted/40 font-mono" />
            </F>
            <F label="Legal form">
              <Select value={form.legal_form} onValueChange={(v) => setForm({ ...form, legal_form: v })}>
                <SelectTrigger data-testid="org-legalform-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["trust", "society", "section_8_company", "religious_institution", "foundation",
                    "international_ngo", "community_organization", "federation", "grant_making_foundation"].map((f) => (
                    <SelectItem key={f} value={f}>{f.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Charity classification">
              <Select value={form.charity_classification}
                      onValueChange={(v) => setForm({ ...form, charity_classification: v })}>
                <SelectTrigger data-testid="org-classification-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["humanitarian", "education", "health", "religious", "environment", "arts", "research"].map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Tax status">
              <Select value={form.tax_status} onValueChange={(v) => setForm({ ...form, tax_status: v })}>
                <SelectTrigger data-testid="org-taxstatus-select" className="bg-muted/40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["tax_exempt", "partially_exempt", "taxable", "pending"].map((f) => (
                    <SelectItem key={f} value={f}>{f.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
            <F label="Timezone">
              <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                     data-testid="org-timezone-input" className="bg-muted/40 font-mono text-xs" />
            </F>
          </div>
        </Panel>

        <Panel testId="panel-org-hierarchy" kicker="FR-ORG-002" title="Entity hierarchy">
          <Table testId="entities-table" head={<><Th>Entity</Th><Th>Level</Th><Th>Country</Th><Th>Currency</Th><Th>Legal form</Th></>}>
            {orgs.map((o) => (
              <tr key={o.id} className="row-hover cursor-pointer" data-testid={`entity-row-${o.id}`}
                  onClick={() => openEntity(o)}>
                <Td>
                  <div className="font-medium text-foreground">{o.display_name}</div>
                  <div className="text-[11px] text-muted-foreground">{o.legal_name}</div>
                </Td>
                <Td><Chip className="border-border bg-muted text-foreground/80">{o.entity_level.replace(/_/g, " ")}</Chip></Td>
                <Td className="font-mono text-xs text-primary">{o.country}</Td>
                <Td className="font-mono text-[11px]">{o.base_currency}</Td>
                <Td className="text-[11px] text-muted-foreground">{o.legal_form.replace(/_/g, " ")}</Td>
              </tr>
            ))}
          </Table>
        </Panel>
      </div>

      <Panel testId="panel-registers" kicker="FR-ORG-004 / 005" title="Registration & status registers">
        <Table testId="registers-table"
               head={<><Th>Register</Th><Th>Issuing authority</Th><Th>Number</Th><Th>Valid from</Th><Th>Valid to</Th><Th>Status</Th></>}>
          {(form.registers || []).length === 0 && <Empty colSpan={6} label="No registers recorded" />}
          {(form.registers || []).map((r) => (
            <tr key={r.number} className="row-hover cursor-pointer" data-testid={`register-row-${r.number}`}
                onClick={() => setRegister(r)}>
              <Td className="font-medium text-foreground">{r.register}</Td>
              <Td className="text-muted-foreground">{r.authority}</Td>
              <Td className="font-mono text-primary">{r.number}</Td>
              <Td className="font-mono text-[11px]">{shortDate(r.valid_from)}</Td>
              <Td className="font-mono text-[11px]">{r.valid_to ? shortDate(r.valid_to) : "Perpetual"}</Td>
              <Td>
                <Chip className={r.status === "active"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                  : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"}>{r.status}</Chip>
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <EntitySheet entity={entity} detail={entityDetail} canWrite={can("organization", "write")}
                   onClose={() => { setEntity(null); setEntityDetail(null); }}
                   onRegister={(r) => setRegister(r)}
                   onRefresh={() => openEntity(entity)} />

      <Sheet open={Boolean(register)} onOpenChange={(v) => !v && setRegister(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md"
                      data-testid="register-detail-sheet">
          {register && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">{register.register}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-1.5 text-[12px]">
                {Object.entries(register).map(([k, v]) => (
                  <div key={k} className="flex items-start justify-between gap-3 border-b border-border/60 py-1.5">
                    <span className="text-[11px] text-muted-foreground">{k.replace(/_/g, " ")}</span>
                    <span className="text-right text-foreground">
                      {v === null || v === undefined || v === ""
                        ? (k === "valid_to" ? "Perpetual" : "—")
                        : (typeof v === "object" ? JSON.stringify(v) : String(v))}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11.5px] text-muted-foreground">
                Open the entity to attach this certificate or email the registration details.
              </p>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
